import { Trip } from "@fairfleet/geotab";
import { UseQueryResult, useQueryClient } from "@tanstack/react-query";
import { startOfDay, sub } from "date-fns";
import flatten from "lodash/flatten";
import { useTripsByDeviceAndDateRange } from "@/hooks/trips/useTripsByDeviceAndDateRange";
import { mapQuery } from "@/utilities/query/mapQuery";

export function usePreviousTrip(trip?: Trip) {
  const fromCache = usePreviousTripFromQueryClient(trip);
  const result = useTripsByDeviceAndDateRange({
    deviceId: trip?.device.id,
    fromDate: trip?.start ? startOfDay(sub(trip.start, { days: 1 })) : undefined,
    toDate: trip?.start,
    enabled: !fromCache,
  });

  if (fromCache) {
    return {
      data: fromCache,
      isLoading: false,
    } as UseQueryResult<Trip | undefined>;
  }

  return mapQuery(result, trips => findPrevTrip(trip, trips));
}

function usePreviousTripFromQueryClient(trip?: Trip) {
  const queryClient = useQueryClient();
  const queries = queryClient.getQueriesData({
    queryKey: ["trips"],
    exact: false,
  });

  return findPrevTrip(
    trip,
    flatten(queries.map(q => q[1] as Trip[] | undefined).filter((t): t is Trip[] => !!t)),
  );
}

function findPrevTrip(trip?: Trip, trips: Trip[] = []) {
  return trips.find(t => t.nextTripStart.getTime() === trip?.start?.getTime());
}
